// ProspectMemoryPanel.jsx — Moonshot #3 UI.
// Renders the persistent memory graph for a lead inside the lead drawer.
// Grouped collapsible categories, live socket updates, manual add/edit/remove.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Brain, Phone, MessageSquare, Mail, FileText, User, Plus, Edit2, Trash2,
  ChevronDown, ChevronRight, Clock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost, apiPatch, apiDel } from '../lib/api.js';
import { getSocket } from '../lib/socket.js';
import { fmtFromNow } from '../lib/format.js';

const CATEGORIES = [
  { key: 'family',            label: 'Family' },
  { key: 'financial',         label: 'Financial' },
  { key: 'health',            label: 'Health' },
  { key: 'objection',         label: 'Objections raised' },
  { key: 'preference',        label: 'Preferences' },
  { key: 'context',           label: 'Context' },
  { key: 'coverage_history',  label: 'Coverage history' },
  { key: 'quote_history',     label: 'Prior quotes' },
  { key: 'commitment',        label: 'Commitments' },
  { key: 'sentiment_signal',  label: 'Sentiment signals' },
];

const SOURCE_ICONS = {
  call:       Phone,
  sms:        MessageSquare,
  email:      Mail,
  form:       FileText,
  manual:     User,
  agent_note: User,
};

export default function ProspectMemoryPanel({ leadId }) {
  const [memory, setMemory] = useState({});
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(() => new Set(['family', 'financial', 'objection', 'commitment']));
  const [addingOpen, setAddingOpen] = useState(false);
  const [addCategory, setAddCategory] = useState('context');
  const [addFact, setAddFact] = useState('');

  const refresh = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const r = await apiGet(`/leads/${leadId}/memory`);
      if (r?.ok) setMemory(r.memory || {});
    } catch (e) {
      // Soft failure — the panel still renders but empty.
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Live updates: server emits prospectmemory:updated to lead:<id> room.
  useEffect(() => {
    if (!leadId) return;
    const socket = getSocket();
    if (!socket) return;
    socket.emit?.('lead:open', leadId);
    const handler = (payload) => {
      if (String(payload?.leadId) === String(leadId)) refresh();
    };
    socket.on('prospectmemory:updated', handler);
    return () => {
      socket.off?.('prospectmemory:updated', handler);
      socket.emit?.('lead:close', leadId);
    };
  }, [leadId, refresh]);

  const toggleCategory = (cat) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const totalCount = useMemo(() => {
    return CATEGORIES.reduce((acc, c) => acc + (memory[c.key]?.length || 0), 0);
  }, [memory]);

  async function handleAdd() {
    if (!addFact.trim()) return;
    try {
      const r = await apiPost(`/leads/${leadId}/memory`, {
        category: addCategory,
        fact: addFact.trim(),
      });
      if (r?.ok) {
        toast.success('Memory added');
        setAddFact('');
        setAddingOpen(false);
        refresh();
      } else {
        toast.error(r?.error || 'Failed to add memory');
      }
    } catch (e) {
      toast.error('Failed to add memory');
    }
  }

  async function handleEdit(node) {
    const next = window.prompt('Edit memory:', node.fact);
    if (next == null || next.trim() === node.fact) return;
    try {
      const r = await apiPatch(`/leads/${leadId}/memory/${node._id}`, { fact: next.trim() });
      if (r?.ok) { toast.success('Updated'); refresh(); }
    } catch (e) { toast.error('Failed to update'); }
  }

  async function handleRemove(node) {
    if (!window.confirm('Remove this memory? It will be soft-deleted (audit trail preserved).')) return;
    try {
      const r = await apiDel(`/leads/${leadId}/memory/${node._id}`);
      if (r?.ok) { toast.success('Removed'); refresh(); }
    } catch (e) { toast.error('Failed to remove'); }
  }

  return (
    <div className="panel-light p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-civix-purple" />
          <span className="text-sm font-semibold">Prospect Memory</span>
          <span className="text-xs text-white/40">{totalCount} facts</span>
        </div>
        <button
          onClick={() => setAddingOpen((v) => !v)}
          className="text-xs px-2 py-1 rounded-sm border border-civix-border hover:border-civix-purple/60 flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>

      {addingOpen && (
        <div className="border border-civix-border rounded-lg p-2 space-y-2">
          <select
            value={addCategory}
            onChange={(e) => setAddCategory(e.target.value)}
            className="w-full bg-civix-panel border border-civix-border rounded-sm px-2 py-1 text-xs"
          >
            {CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
          <textarea
            value={addFact}
            onChange={(e) => setAddFact(e.target.value)}
            placeholder="e.g. Spouse Carla (41) also wants coverage"
            rows={2}
            className="w-full bg-civix-panel border border-civix-border rounded-sm px-2 py-1 text-xs"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setAddingOpen(false)} className="text-xs px-2 py-1 rounded-sm hover:bg-civix-panelLight">Cancel</button>
            <button onClick={handleAdd} className="text-xs px-2 py-1 rounded-sm bg-civix-purple/30 border border-civix-purple/40 hover:bg-civix-purple/40">Save</button>
          </div>
        </div>
      )}

      {loading && totalCount === 0 && (
        <div className="text-xs text-white/40">Loading memory…</div>
      )}
      {!loading && totalCount === 0 && (
        <div className="text-xs text-white/40">
          No durable facts yet. Memory builds automatically from calls and SMS.
        </div>
      )}

      <div className="space-y-1">
        {CATEGORIES.map((c) => {
          const items = memory[c.key] || [];
          if (!items.length) return null;
          const isOpen = open.has(c.key);
          return (
            <div key={c.key} className="border border-civix-border/60 rounded-lg">
              <button
                onClick={() => toggleCategory(c.key)}
                className="w-full flex items-center justify-between px-2 py-1.5 hover:bg-civix-panelLight rounded-lg"
              >
                <div className="flex items-center gap-2 text-xs">
                  {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  <span className="font-medium">{c.label}</span>
                  <span className="text-white/40">({items.length})</span>
                </div>
              </button>
              {isOpen && (
                <ul className="px-2 pb-2 space-y-1.5">
                  {items.map((n) => {
                    const SrcIcon = SOURCE_ICONS[n.source?.kind] || FileText;
                    const conf = Math.round((n.confidence || 0) * 100);
                    return (
                      <li key={n._id} className="border border-civix-border/40 rounded-sm p-2 text-xs">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="text-white/90">{n.fact}</div>
                            <div className="flex items-center gap-2 text-[10px] text-white/40 mt-1">
                              <SrcIcon className="w-3 h-3" />
                              <span>{n.source?.kind || 'unknown'}</span>
                              <span className="inline-flex items-center gap-0.5">
                                <span className={`w-1.5 h-1.5 rounded-full ${conf >= 80 ? 'bg-green-400' : conf >= 50 ? 'bg-yellow-400' : 'bg-red-400'}`}></span>
                                {conf}%
                              </span>
                              <Clock className="w-3 h-3" />
                              <span>{fmtFromNow(n.generatedAt || n.createdAt)}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-60 hover:opacity-100">
                            <button onClick={() => handleEdit(n)} className="p-1 hover:bg-civix-panelLight rounded-sm" title="Edit">
                              <Edit2 className="w-3 h-3" />
                            </button>
                            <button onClick={() => handleRemove(n)} className="p-1 hover:bg-civix-panelLight rounded-sm" title="Remove">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
